import React, { useState, useEffect } from 'react';
import { useLocation, Router } from 'react-router-dom';
import dateToLocaleString from 'Utils/DateToLocaleString';
import SideBarContainer from 'Containers/SideBarContainer';
import Chip from 'Components/Chip';
import useTranslation from 'Hooks/useTranslation';
import DraftChip from 'Components/DraftChip';
import TabPage from 'Components/TabPage';
import { useSelector } from 'react-redux';
import { userSelector } from 'Redux/LoggedUserSlice';
import { ROLE_ADMINISTRATOR } from 'Constants/General';
import { CheckOutlined } from '@mui/icons-material';
import Information from './Pages/Information';
import Lessons from './Pages/Lessons';
import Documents from './Pages/Documents';
import Members from './Pages/Members';
import Dates from './Pages/Dates';
import Evaluations from './Pages/Evaluations';
import Certificate from './Pages/Certificate';
import style from './style';
import CourseThumbnail from './Components/CourseThumbnail';
import useCourseDetails from './Hooks/useCourseDetails';
import CourseEditButton from './Components/CourseEditButton';
import { s3 } from 'Services/AWS/s3';

const {
  ContainerCourse,
  ContainerHeader,
  HeaderContent,
  HeaderInfo,
  HeaderTitle,
  HeaderSubTitle,
  checkIcon,
  LastUpdatedContainer,
  LastUpdated,
  lastUpdatedLabel,
  lastUpdatedText,
} = style;

export default function Course() {
  const { t2 } = useTranslation('CourseDetails');
  const location = useLocation();

  const isComingFrom = location?.state?.isComingFrom;

  // Información relacionada al curso y el color de la categoría (para el chip)
  const { course, colorCategory } = location?.state || {};
  const { id } = useSelector(userSelector);

  const { course: currentCourse, loading } = useCourseDetails({
    courseId: course?.[course?.length - 1],
  });

  // === Información del curso===
  // Donde:
  // course[0] = Título
  // course[1] = Descripción
  // course[2] = Prioridad
  // course[3] = Categoría
  // course[4] = Autor
  // course[5] = Imagen del usuario
  // course[6] = Imagen S3 del curso
  // course[7] = Fecha de actualización
  // course[8] = Si es un borrador
  const title = course[0];
  const courseCategory = course[3];
  const courseAuthor = course[4];
  const image = course?.[6];
  const formattedDate = dateToLocaleString(course?.[7]);

  // Hooks de la página
  const [isLoading, setIsLoading] = useState(image !== null);
  const [fetchedImage, setFetchedImage] = useState(null);

  const { roleId } = useSelector(userSelector);

  // Precarga la imagen del curso al momento de cargar la página
  useEffect(() => {
    // Si no hay imagen se ignora la acción.
    if (!image) return;

    s3.getSignedUrl(image)
      .then(url => {
        setFetchedImage(url?.signedUrl);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [image]);

  const tabsTitle = [
    t2('BasicInfo'),
    t2('Lessons'),
    t2('Documents'),
    t2('Members'),
    t2('Dates'),
    t2('Evaluations'),
    t2('Certification'),
  ];

  const pages = [
    <Information key="information" course={course} />,
    <Lessons key="lessons" course={course} />,
    <Documents key="documents" course={course} />,
    <Members key="members" course={course} />,
    <Dates key="dates" course={course} />,
    <Evaluations key="evaluations" course={course} />,
    <Certificate key="certificate" course={course} />,
  ];

  if (isComingFrom !== '/cursos' && isComingFrom !== '/mis-cursos' && isComingFrom !== '/dashboard') {
    return <Router to="/dashboard" />;
  }

  if (isComingFrom === '/cursos/editar') {
    return <Router to="/cursos" />;
  }

  return (
    <SideBarContainer>
      <ContainerCourse>
        <ContainerHeader>
          <HeaderContent>
            <CourseThumbnail image={fetchedImage} loading={isLoading} />
            <HeaderInfo>
              <HeaderTitle>
                <div>{title}</div>
                <Chip
                  style={{
                    border: 'none',
                    background: colorCategory,
                    color: 'white',
                  }}
                >
                  {null}
                  <h4>{courseCategory}</h4>
                </Chip>
                <DraftChip visible={!!course[8]} />
              </HeaderTitle>
              <HeaderSubTitle>
                <div>
                  {t2('By')} {courseAuthor}
                </div>
                <LastUpdatedContainer>
                  <CheckOutlined style={checkIcon} />
                  <LastUpdated>
                    <div style={lastUpdatedLabel}>{t2('LastUpdated')}:</div>
                    <div style={lastUpdatedText}>{formattedDate}</div>
                  </LastUpdated>
                </LastUpdatedContainer>
              </HeaderSubTitle>
            </HeaderInfo>
            {currentCourse && (
              <CourseEditButton
                course={course}
                visible={currentCourse.userCreatedId === id || roleId === ROLE_ADMINISTRATOR}
              />
            )}
          </HeaderContent>
        </ContainerHeader>
        <TabPage tabsTitle={tabsTitle} pages={pages} />
      </ContainerCourse>
    </SideBarContainer>
  );
}
